// src/components/AddressBookModal.js
//
// Address Book: a global lookup modal for area service providers (gas/LP
// stations, food/clothing/baby pantries, firewood, state services, etc.).
// Opened from the navbar. Filters combine (AND): free-text search across the
// top, a type dropdown, and an A–Z rail down the left keyed on the contact's
// first letter. ADMIN/DIRECTOR/COORDINATOR/INTAKE_WORKER can add and edit
// contacts; hard-delete and managing the type list stay ADMIN/DIRECTOR-only.
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Alert, Badge, Button, Col, Form, InputGroup, Modal, Row, Spinner, Table,
} from "react-bootstrap";
import api from "../api";
import { AuthContext } from "../AuthContext";
import AutoGrowTextarea from "./AutoGrowTextarea";

const LETTERS = ["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")];

const EMPTY_FORM = {
  name: "", category_id: "", contact_person: "", phone: "", email: "",
  website: "", address: "", city: "", state: "", zipcode: "", hours: "",
  notes: "", active: true,
};

// Bucket a name under its uppercase first letter; non-letters land in "#".
function firstLetter(name) {
  const ch = (name || "").trim().charAt(0).toUpperCase();
  return ch >= "A" && ch <= "Z" ? ch : "#";
}

function isAdmin(user) {
  const roles = (user?.roles || []).map((r) => String(r).toUpperCase());
  return roles.includes("ADMIN") || roles.includes("DIRECTOR");
}

// Roles allowed to add/edit contacts — mirrors the backend EDIT_ROLES
// (ADMIN/DIRECTOR + COORDINATOR + INTAKE_WORKER). Hard-delete + category
// management stay ADMIN-only (see `admin` below).
function canEditContacts(user) {
  const roles = (user?.roles || []).map((r) => String(r).toUpperCase());
  return ["ADMIN", "DIRECTOR", "COORDINATOR", "INTAKE_WORKER"].some((r) => roles.includes(r));
}

export default function AddressBookModal({ show, onHide }) {
  const { user } = useContext(AuthContext);
  const admin = isAdmin(user);
  const canEdit = canEditContacts(user);

  const [contacts, setContacts]   = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");

  // Filters
  const [search, setSearch]       = useState("");
  const [typeFilter, setTypeFilter] = useState(""); // "" = all types
  const [letter, setLetter]       = useState("ALL");
  const [showInactive, setShowInactive] = useState(false);

  // View within the modal: list | form | types
  const [view, setView]           = useState("list");

  // Contact form state
  const [form, setForm]           = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving]       = useState(false);
  const [actionError, setActionError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const load = useCallback(async () => {
    setError("");
    try {
      const [cRes, catRes] = await Promise.all([
        api.get("/address-book/contacts"),
        api.get("/address-book/categories"),
      ]);
      setContacts(cRes.data.items || []);
      setCategories(catRes.data.items || []);
    } catch {
      setError("Failed to load the address book.");
    } finally {
      setLoading(false);
    }
  }, []);

  // (Re)load whenever the modal is opened; reset to the list view.
  useEffect(() => {
    if (show) {
      setLoading(true);
      setView("list");
      load();
    }
  }, [show, load]);

  function field(key) {
    return (e) => setForm((p) => ({ ...p, [key]: e.target.value }));
  }

  // ----- Filtering pipeline (client-side; dataset is modest) ---------------
  const baseFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return contacts
      .filter((c) => showInactive || c.active)
      .filter((c) => typeFilter === "" || String(c.category_id) === typeFilter)
      .filter((c) => {
        if (!q) return true;
        return [c.name, c.city, c.phone, c.contact_person]
          .some((v) => (v || "").toLowerCase().includes(q));
      });
  }, [contacts, search, typeFilter, showInactive]);

  // Which letters have at least one match under the current search/type filter.
  const availableLetters = useMemo(() => {
    const s = new Set();
    baseFiltered.forEach((c) => s.add(firstLetter(c.name)));
    return s;
  }, [baseFiltered]);

  const visible = useMemo(() => {
    const rows = letter === "ALL"
      ? baseFiltered
      : baseFiltered.filter((c) => firstLetter(c.name) === letter);
    return [...rows].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  }, [baseFiltered, letter]);

  // ----- Contact add / edit ------------------------------------------------
  function openAdd() {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setActionError("");
    setConfirmDelete(false);
    setView("form");
  }

  function openEdit(c) {
    setForm({
      name: c.name ?? "",
      category_id: c.category_id != null ? String(c.category_id) : "",
      contact_person: c.contact_person ?? "",
      phone: c.phone ?? "",
      email: c.email ?? "",
      website: c.website ?? "",
      address: c.address ?? "",
      city: c.city ?? "",
      state: c.state ?? "",
      zipcode: c.zipcode ?? "",
      hours: c.hours ?? "",
      notes: c.notes ?? "",
      active: !!c.active,
    });
    setEditingId(c.id);
    setActionError("");
    setConfirmDelete(false);
    setView("form");
  }

  async function handleSave(e) {
    e.preventDefault();
    if (!form.name.trim()) {
      setActionError("Name is required.");
      return;
    }
    if (!form.category_id) {
      setActionError("Type is required.");
      return;
    }
    setSaving(true);
    setActionError("");
    const payload = {
      name: form.name.trim(),
      category_id: Number(form.category_id),
      contact_person: form.contact_person.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      website: form.website.trim(),
      address: form.address.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      zipcode: form.zipcode.trim(),
      hours: form.hours.trim(),
      notes: form.notes.trim(),
      active: !!form.active,
    };
    try {
      if (editingId) {
        await api.put(`/address-book/contacts/${editingId}`, payload);
      } else {
        await api.post("/address-book/contacts", payload);
      }
      await load();
      setView("list");
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to save contact.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    setSaving(true);
    setActionError("");
    try {
      await api.delete(`/address-book/contacts/${editingId}`);
      await load();
      setView("list");
    } catch (err) {
      setActionError(err.response?.data?.error || "Failed to delete contact.");
    } finally {
      setSaving(false);
    }
  }

  // Categories offered in the form: active ones, plus the contact's current
  // one even if it has since been retired (so editing doesn't silently drop it).
  const formCategories = useMemo(() => {
    const list = categories.filter((c) => c.active);
    if (form.category_id) {
      const cur = categories.find((c) => String(c.id) === form.category_id);
      if (cur && !cur.active) list.push(cur);
    }
    return list;
  }, [categories, form.category_id]);

  function renderList() {
    return (
      <>
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <InputGroup style={{ maxWidth: 360, flex: "1 1 240px" }}>
            <InputGroup.Text><i className="bi bi-search" /></InputGroup.Text>
            <Form.Control
              placeholder="Search name, city, phone…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            {search && (
              <Button variant="outline-secondary" onClick={() => setSearch("")}>
                Clear
              </Button>
            )}
          </InputGroup>

          <Form.Select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setLetter("ALL"); }}
            style={{ maxWidth: 220 }}
          >
            <option value="">All Types</option>
            {categories.map((c) => (
              <option key={c.id} value={String(c.id)}>{c.name}</option>
            ))}
          </Form.Select>

          <Form.Check
            type="checkbox"
            id="ab-show-inactive"
            label="Show inactive"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
            className="ms-1"
          />

          {canEdit && (
            <Button size="sm" className="ms-auto" onClick={openAdd}>
              <i className="bi bi-plus-lg me-1" />Add Contact
            </Button>
          )}
        </div>

        <Row className="g-3">
          {/* A–Z rail */}
          <Col xs="auto">
            <div className="d-flex flex-column gap-1" style={{ width: 44 }}>
              <Button
                size="sm"
                variant={letter === "ALL" ? "primary" : "outline-secondary"}
                className="py-0 px-1"
                onClick={() => setLetter("ALL")}
              >
                All
              </Button>
              {LETTERS.map((L) => {
                const enabled = availableLetters.has(L);
                return (
                  <Button
                    key={L}
                    size="sm"
                    variant={letter === L ? "primary" : "outline-secondary"}
                    className="py-0 px-1"
                    disabled={!enabled}
                    onClick={() => setLetter(L)}
                  >
                    {L}
                  </Button>
                );
              })}
            </div>
          </Col>

          {/* Results */}
          <Col>
            <div style={{ maxHeight: "58vh", overflowY: "auto" }}>
              {visible.length === 0 ? (
                <p className="text-muted mb-0">No contacts match your filters.</p>
              ) : (
                <Table hover responsive size="sm" className="mb-0">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Type</th>
                      <th>Phone</th>
                      <th>City</th>
                      <th>Hours</th>
                      {canEdit && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((c) => (
                      <tr key={c.id} className={!c.active ? "table-secondary" : ""}>
                        <td className="align-middle fw-semibold">
                          {c.name}
                          {!c.active && (
                            <Badge bg="secondary" className="ms-2">Inactive</Badge>
                          )}
                        </td>
                        <td className="align-middle">
                          {c.category_name
                            ? <Badge bg="info" text="dark">{c.category_name}</Badge>
                            : <span className="text-muted fst-italic">—</span>}
                        </td>
                        <td className="align-middle">
                          {c.phone
                            ? <a href={`tel:${c.phone}`}>{c.phone}</a>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td className="align-middle">{c.city || <span className="text-muted">—</span>}</td>
                        <td className="align-middle text-muted small">{c.hours || "—"}</td>
                        {canEdit && (
                          <td className="align-middle text-end">
                            <Button size="sm" variant="outline-primary" onClick={() => openEdit(c)}>
                              <i className="bi bi-pencil" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </div>
          </Col>
        </Row>
      </>
    );
  }

  function renderForm() {
    return (
      <Form onSubmit={handleSave}>
        <Button variant="link" className="px-0 mb-2" onClick={() => setView("list")}>
          <i className="bi bi-arrow-left me-1" />Back to list
        </Button>
        {actionError && <Alert variant="danger" className="py-2">{actionError}</Alert>}

        <Row className="g-3">
          <Col md={8}>
            <Form.Group>
              <Form.Label>Name <span className="text-danger">*</span></Form.Label>
              <Form.Control value={form.name} onChange={field("name")} autoFocus />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Type <span className="text-danger">*</span></Form.Label>
              <Form.Select value={form.category_id} onChange={field("category_id")}>
                <option value="">Select…</option>
                {formCategories.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Contact Person</Form.Label>
              <Form.Control value={form.contact_person} onChange={field("contact_person")} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Phone</Form.Label>
              <Form.Control value={form.phone} onChange={field("phone")} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Hours</Form.Label>
              <Form.Control value={form.hours} onChange={field("hours")} placeholder="M–F 9–5" />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={form.email} onChange={field("email")} />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group>
              <Form.Label>Website</Form.Label>
              <Form.Control value={form.website} onChange={field("website")} placeholder="https://…" />
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label>Address</Form.Label>
              <Form.Control value={form.address} onChange={field("address")} />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label>City</Form.Label>
              <Form.Control value={form.city} onChange={field("city")} />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>State</Form.Label>
              <Form.Control value={form.state} onChange={field("state")} />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group>
              <Form.Label>Zip</Form.Label>
              <Form.Control value={form.zipcode} onChange={field("zipcode")} />
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <AutoGrowTextarea rows={2} value={form.notes} onChange={field("notes")} />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Check
              type="switch"
              id="ab-form-active"
              label="Active"
              checked={form.active}
              onChange={(e) => setForm((p) => ({ ...p, active: e.target.checked }))}
            />
          </Col>
        </Row>

        <div className="d-flex align-items-center gap-2 mt-4">
          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? <Spinner animation="border" size="sm" /> : (editingId ? "Save Changes" : "Create Contact")}
          </Button>
          <Button variant="secondary" onClick={() => setView("list")} disabled={saving}>
            Cancel
          </Button>
          {admin && editingId && (
            <div className="ms-auto">
              {confirmDelete ? (
                <>
                  <span className="text-danger me-2 small">Delete permanently?</span>
                  <Button size="sm" variant="danger" onClick={handleDelete} disabled={saving}>
                    Yes, delete
                  </Button>
                  <Button size="sm" variant="link" onClick={() => setConfirmDelete(false)} disabled={saving}>
                    No
                  </Button>
                </>
              ) : (
                <Button size="sm" variant="outline-danger" onClick={() => setConfirmDelete(true)} disabled={saving}>
                  <i className="bi bi-trash me-1" />Delete
                </Button>
              )}
            </div>
          )}
        </div>
      </Form>
    );
  }

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-journal-bookmark me-2" />Address Book
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="py-4 text-center">
            <Spinner animation="border" size="sm" className="me-2" />Loading…
          </div>
        ) : view === "form" ? (
          renderForm()
        ) : view === "types" ? (
          <ManageTypes
            categories={categories}
            onChanged={load}
            onBack={() => setView("list")}
          />
        ) : (
          renderList()
        )}
      </Modal.Body>
      {view === "list" && !loading && (
        <Modal.Footer>
          {admin && (
            <Button variant="outline-secondary" size="sm" className="me-auto" onClick={() => setView("types")}>
              <i className="bi bi-tags me-1" />Manage types
            </Button>
          )}
          <Button variant="secondary" onClick={onHide}>Close</Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Manage types (ADMIN only): add / rename / retire / delete categories.
// ---------------------------------------------------------------------------
function ManageTypes({ categories, onChanged, onBack }) {
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [confirmId, setConfirmId] = useState(null);

  async function run(fn) {
    setBusy(true);
    setErr("");
    try {
      await fn();
      await onChanged();
    } catch (e) {
      setErr(e.response?.data?.error || "Action failed.");
    } finally {
      setBusy(false);
    }
  }

  async function add(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    await run(async () => {
      await api.post("/address-book/categories", {
        name: newName.trim(),
        sort_order: (categories.length + 1) * 10,
      });
      setNewName("");
    });
  }

  return (
    <>
      <Button variant="link" className="px-0 mb-2" onClick={onBack}>
        <i className="bi bi-arrow-left me-1" />Back to list
      </Button>
      {err && <Alert variant="danger" className="py-2">{err}</Alert>}

      <Form onSubmit={add} className="d-flex gap-2 mb-3" style={{ maxWidth: 420 }}>
        <Form.Control
          placeholder="New type (e.g. Firewood, Baby Pantry)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />
        <Button type="submit" disabled={busy || !newName.trim()}>Add</Button>
      </Form>

      <Table size="sm" hover responsive>
        <thead>
          <tr><th>Type</th><th>Contacts</th><th>Status</th><th /></tr>
        </thead>
        <tbody>
          {categories.map((c) => (
            <tr key={c.id} className={!c.active ? "table-secondary" : ""}>
              <td className="align-middle">
                <InlineRename category={c} disabled={busy} onSave={(name) =>
                  run(() => api.put(`/address-book/categories/${c.id}`, { name }))
                } />
              </td>
              <td className="align-middle">{c.contact_count ?? 0}</td>
              <td className="align-middle">
                {c.active
                  ? <Badge bg="success">Active</Badge>
                  : <Badge bg="secondary">Retired</Badge>}
              </td>
              <td className="align-middle text-end">
                <Button
                  size="sm"
                  variant={c.active ? "outline-secondary" : "outline-success"}
                  className="me-2"
                  disabled={busy}
                  onClick={() => run(() => api.put(`/address-book/categories/${c.id}`, { active: !c.active }))}
                >
                  {c.active ? "Retire" : "Restore"}
                </Button>
                {confirmId === c.id ? (
                  <>
                    <Button size="sm" variant="danger" disabled={busy}
                      onClick={() => run(async () => { await api.delete(`/address-book/categories/${c.id}`); setConfirmId(null); })}>
                      Confirm
                    </Button>
                    <Button size="sm" variant="link" disabled={busy} onClick={() => setConfirmId(null)}>Cancel</Button>
                  </>
                ) : (
                  <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => setConfirmId(c.id)}>
                    <i className="bi bi-trash" />
                  </Button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
      <p className="text-muted small mb-0">
        A type that is still used by contacts can’t be deleted — retire it instead.
      </p>
    </>
  );
}

function InlineRename({ category, onSave, disabled }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(category.name);

  if (!editing) {
    return (
      <span role="button" className="text-decoration-underline" onClick={() => { setName(category.name); setEditing(true); }}>
        {category.name}
      </span>
    );
  }
  return (
    <InputGroup size="sm" style={{ maxWidth: 260 }}>
      <Form.Control value={name} onChange={(e) => setName(e.target.value)} disabled={disabled} autoFocus />
      <Button variant="primary" disabled={disabled || !name.trim()}
        onClick={() => { onSave(name.trim()); setEditing(false); }}>
        Save
      </Button>
      <Button variant="outline-secondary" disabled={disabled} onClick={() => setEditing(false)}>×</Button>
    </InputGroup>
  );
}
